import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, map, tap, throwError } from 'rxjs';
import { ApiResponse } from '../../../../../shared/dtos/api-response';
import { ApiError } from '../../../../../shared/dtos/api-error';
import { CreditPlanMapper } from '../../models/credit-plan';
import type { CreditPlan } from '../../models/credit-plan';
import { CreditPlansStore } from '../../stores/credit-plans.store';
import {
  INTERNAL_PLWC_CREDIT_PLAN_ENDPOINT,
  INTERNAL_PLWC_CREDIT_PLAN_LOAN_TEMPLATES_ENDPOINT,
  INTERNAL_PLWC_CREDIT_PLANS_ENDPOINT,
} from '../../constants/endpoints';
import type {
  CreditPlanCreateRequest,
  CreditPlanSummary,
  CreditPlanUpdateRequest,
} from '../../dtos/credit-plan';

@Injectable({ providedIn: 'root' })
export class CreditPlanService {
  private readonly http = inject(HttpClient);
  private readonly store = inject(CreditPlansStore);

  getCreditPlans(): Observable<CreditPlan[]> {
    this.store.setLoading();
    return this.http.get<ApiResponse<CreditPlanSummary[]>>(INTERNAL_PLWC_CREDIT_PLANS_ENDPOINT).pipe(
      map((resp) => CreditPlanMapper.fromDtoList(resp.data)),
      tap((records) => this.store.setRecords(records)),
      catchError((err: HttpErrorResponse) => {
        const body = err.error as ApiError | undefined;
        this.store.setError(body?.message ?? 'Failed to load credit plans.');
        return throwError(() => body ?? { message: 'Failed to load credit plans.' });
      }),
    );
  }

  getLoanTemplates(): Observable<string[]> {
    return this.http
      .get<ApiResponse<string[]>>(INTERNAL_PLWC_CREDIT_PLAN_LOAN_TEMPLATES_ENDPOINT)
      .pipe(map((resp) => resp.data ?? []));
  }

  createCreditPlan(body: CreditPlanCreateRequest): Observable<CreditPlan> {
    return this.http
      .post<ApiResponse<CreditPlanSummary>>(INTERNAL_PLWC_CREDIT_PLANS_ENDPOINT, body)
      .pipe(
        map((resp) => CreditPlanMapper.fromDto(resp.data)),
        tap((record) => this.store.addRecord(record)),
        catchError((err: HttpErrorResponse) => {
          const body = err.error as ApiError | undefined;
          return throwError(() => body ?? { message: 'Failed to create credit plan.' });
        }),
      );
  }

  updateCreditPlan(creditPlanId: number, body: CreditPlanUpdateRequest): Observable<CreditPlan> {
    return this.http
      .put<ApiResponse<CreditPlanSummary>>(INTERNAL_PLWC_CREDIT_PLAN_ENDPOINT(creditPlanId), body)
      .pipe(
        map((resp) => CreditPlanMapper.fromDto(resp.data)),
        tap((record) => this.store.updateRecord(record)),
        catchError((err: HttpErrorResponse) => {
          const body = err.error as ApiError | undefined;
          return throwError(() => body ?? { message: 'Failed to update credit plan.' });
        }),
      );
  }
}
